/*
  CTR media zoom — upgrades the stock product media modal into a proper viewer:
  swipe between photos, pinch to zoom on touch, wheel/double-click on desktop,
  drag to pan while zoomed. Pure enhancement: if the expected structure isn't
  there it quietly does nothing and the modal keeps its default behaviour.
*/
(function () {
  var MIN = 1;
  var MAX = 4;

  function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }

  function Pane(el, viewer) {
    this.el = el;
    this.viewer = viewer;
    this.img = el.tagName === 'IMG' ? el : el.querySelector('img');
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.startDist = 0;
    this.startScale = 1;
    this.panning = false;
    this.lastX = 0;
    this.lastY = 0;
    this.lastTap = 0;
    if (this.img) this.bind();
  }

  Pane.prototype.apply = function () {
    this.img.style.transform =
      'translate3d(' + this.tx + 'px,' + this.ty + 'px,0) scale(' + this.scale + ')';
    this.el.classList.toggle('ctr-zoomed', this.scale > 1.01);
    this.viewer.setLocked(this.scale > 1.01);
  };

  Pane.prototype.reset = function () {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.img.style.transition = 'transform .2s ease';
    this.apply();
    var self = this;
    setTimeout(function () {
      self.img.style.transition = '';
    }, 220);
  };

  // keep the image from being dragged past its own edges
  Pane.prototype.clampPan = function () {
    var rect = this.el.getBoundingClientRect();
    var w = (this.img.clientWidth * this.scale - rect.width) / 2;
    var h = (this.img.clientHeight * this.scale - rect.height) / 2;
    this.tx = clamp(this.tx, -Math.max(0, w), Math.max(0, w));
    this.ty = clamp(this.ty, -Math.max(0, h), Math.max(0, h));
  };

  Pane.prototype.zoomTo = function (next, cx, cy) {
    var rect = this.el.getBoundingClientRect();
    var px = (cx == null ? rect.width / 2 : cx - rect.left) - rect.width / 2;
    var py = (cy == null ? rect.height / 2 : cy - rect.top) - rect.height / 2;
    var prev = this.scale;
    this.scale = clamp(next, MIN, MAX);
    var k = this.scale / prev;
    this.tx = (this.tx - px) * k + px;
    this.ty = (this.ty - py) * k + py;
    if (this.scale <= 1.01) {
      this.tx = 0;
      this.ty = 0;
    } else {
      this.clampPan();
    }
    this.apply();
  };

  Pane.prototype.bind = function () {
    var self = this;
    var img = this.img;

    img.addEventListener('dblclick', function (e) {
      e.preventDefault();
      self.zoomTo(self.scale > 1.01 ? 1 : 2.5, e.clientX, e.clientY);
    });

    img.addEventListener(
      'wheel',
      function (e) {
        if (!e.ctrlKey && Math.abs(e.deltaY) < 2) return;
        e.preventDefault();
        self.zoomTo(self.scale * (e.deltaY > 0 ? 0.9 : 1.1), e.clientX, e.clientY);
      },
      { passive: false }
    );

    img.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length === 2) {
          self.startDist = dist(e.touches);
          self.startScale = self.scale;
        } else if (e.touches.length === 1) {
          var now = Date.now();
          if (now - self.lastTap < 300) {
            e.preventDefault();
            self.zoomTo(self.scale > 1.01 ? 1 : 2.5, e.touches[0].clientX, e.touches[0].clientY);
          }
          self.lastTap = now;
          if (self.scale > 1.01) {
            self.panning = true;
            self.lastX = e.touches[0].clientX;
            self.lastY = e.touches[0].clientY;
          }
        }
      },
      { passive: false }
    );

    img.addEventListener(
      'touchmove',
      function (e) {
        if (e.touches.length === 2 && self.startDist) {
          e.preventDefault();
          var mid = midpoint(e.touches);
          self.zoomTo((dist(e.touches) / self.startDist) * self.startScale, mid.x, mid.y);
        } else if (self.panning && e.touches.length === 1) {
          e.preventDefault();
          self.tx += e.touches[0].clientX - self.lastX;
          self.ty += e.touches[0].clientY - self.lastY;
          self.lastX = e.touches[0].clientX;
          self.lastY = e.touches[0].clientY;
          self.clampPan();
          self.apply();
        }
      },
      { passive: false }
    );

    img.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) {
        self.panning = false;
        self.startDist = 0;
        if (self.scale <= 1.01) self.reset();
      }
    });

    // desktop drag while zoomed
    img.addEventListener('mousedown', function (e) {
      if (self.scale <= 1.01) return;
      e.preventDefault();
      self.panning = true;
      self.lastX = e.clientX;
      self.lastY = e.clientY;
    });
    window.addEventListener('mousemove', function (e) {
      if (!self.panning) return;
      self.tx += e.clientX - self.lastX;
      self.ty += e.clientY - self.lastY;
      self.lastX = e.clientX;
      self.lastY = e.clientY;
      self.clampPan();
      self.apply();
    });
    window.addEventListener('mouseup', function () {
      self.panning = false;
    });
  };

  function dist(t) {
    var dx = t[0].clientX - t[1].clientX;
    var dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function midpoint(t) {
    return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 };
  }

  function Viewer(modal) {
    this.modal = modal;
    this.track = modal.querySelector('[role="document"]');
    if (!this.track) return;
    this.track.classList.add('ctr-zoom-track');
    this.panes = [];
    var self = this;
    // Children here are the <img> elements themselves (not wrappers), so give
    // each one a box of its own — that box is what snaps, clips and zooms.
    Array.prototype.slice.call(this.track.children).forEach(function (child) {
      var pane;
      if (child.classList && child.classList.contains('ctr-zoom-pane')) {
        pane = child;
      } else if (child.tagName === 'IMG') {
        pane = document.createElement('div');
        pane.className = 'ctr-zoom-pane';
        self.track.insertBefore(pane, child);
        pane.appendChild(child);
      } else {
        pane = child;
        pane.classList.add('ctr-zoom-pane');
      }
      self.panes.push(new Pane(pane, self));
    });
    this.track.addEventListener('scroll', function () {
      self.panes.forEach(function (p) {
        if (p.img && p.scale > 1.01) p.reset();
      });
    });

    // The stock modal script positions the track by pixel maths meant for its own
    // vertical layout. Once it has opened, snap to the photo the shopper clicked.
    if (window.MutationObserver) {
      this.observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          if (record.attributeName !== 'open') return;
          if (!modal.hasAttribute('open')) return;
          setTimeout(function () { self.snapToActive(); }, 30);
        });
      });
      this.observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  Viewer.prototype.snapToActive = function () {
    var opener = this.modal.openedBy;
    var id = opener && opener.getAttribute && opener.getAttribute('data-media-id');
    var img = id ? this.track.querySelector('[data-media-id="' + id + '"]') : null;
    var pane = img && img.closest ? img.closest('.ctr-zoom-pane') : null;
    this.panes.forEach(function (p) { if (p.img && p.scale > 1.01) p.reset(); });
    if (!pane) return;
    this.track.scrollLeft = pane.offsetLeft;
  };

  Viewer.prototype.setLocked = function (locked) {
    this.track.classList.toggle('ctr-zoom-locked', locked);
  };

  function upgrade(modal) {
    if (modal.ctrZoom) return;
    try {
      modal.ctrZoom = new Viewer(modal);
    } catch (e) {
      /* leave the default modal alone */
    }
  }

  function init() {
    document.querySelectorAll('product-modal').forEach(upgrade);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
